import {Injectable} from '@angular/core';
import {AuditFilters} from "../model/filters/audit-filters";
import {Observable} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {HttpUtils} from "../shared/http.utils";
import {Audit} from '../model/audit-model';
import {FiltersUtils} from "../shared/filters.utils";
import {ExportFileType} from "../model/export-file-type";
import {FileExtensionType} from "../model/file-extension-type";
import {ExportFilePersistRequest} from "../model/requests/export-file-persist-request";
import {ImportFileType} from "../model/import-file-type";
import {ImportFilePersistRequest} from "../model/requests/import-file-persist-request";
import {FileUploadData} from "../model/custom/import-csv/file-upload-data";

@Injectable({
  providedIn: 'root'
})
export class AuditsService {

  private readonly auditsURL: string;


  constructor(private http: HttpClient) {
    this.auditsURL = '/api/audits';
  }

  getAudits(offset: number, limit: number, filters: AuditFilters): Observable<{
    data: { content: Audit[], totalElements: number }
  }> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    if (filters != null) {
      options.params["objectTypes"] = FiltersUtils.getFilter(filters.objectTypes);
      options.params["objectId"] = FiltersUtils.getFilterTrim(filters.objectId);
      options.params["attributeName"] = FiltersUtils.getFilterTrim(filters.attributeName);
      options.params["actions"] = FiltersUtils.getFilter(filters.actions);
      options = AuditsService.setBasicParameters(options, filters);
    }
    return this.http.get<{ data: { content: Audit[], totalElements: number } }>(this.auditsURL, options);
  }

  getMachineAuditsBySerialNumber(serialNumber: string, offset: number, limit: number, filters: AuditFilters): Observable<{
    data: { content: Audit[], totalElements: number }
  }> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    if (filters != null) {
      options = AuditsService.setMachineParameters(options, filters, serialNumber);
    }
    return this.http.get<{ data: { content: Audit[], totalElements: number } }>(this.auditsURL, options);
  }

  getMachineAuditsExcel(serialNumber: string, filters: AuditFilters, fileName: string): Observable<Blob> {
    const url: string = `${this.auditsURL}/excel/${fileName}`;
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    if (filters != null) {
      options = AuditsService.setMachineParameters(options, filters, serialNumber);
    }
    return this.http.get(url, {params: options.params, responseType: 'blob'});
  }

  public createDownloadGraphAudit(exportFileType: ExportFileType, fileExtensionType: FileExtensionType, serialNumber: string): Observable<any> {
    return this.createDownloadAudit(null, null, exportFileType, fileExtensionType, serialNumber);
  }

  public createDownloadAudit(fileName: string, fileSize: number, exportFileType: ExportFileType, fileExtensionType: FileExtensionType,
                             serialNumber: string): Observable<any> {
    const url: string = `${this.auditsURL}/download`;
    let persistData: ExportFilePersistRequest = {
      fileName: fileName,
      fileSize: fileSize,
      exportFileType: exportFileType,
      fileExtensionType: fileExtensionType,
      serialNumber: serialNumber
    };
    return this.http.post<any>(url, persistData);
  }

  public createUploadAudit(files: FileUploadData[], importFileType: ImportFileType, fileExtensionType: FileExtensionType): Observable<any> {
    const url: string = `${this.auditsURL}/upload`;
    let persistData: ImportFilePersistRequest = {
      files: files,
      importFileType: importFileType,
      fileExtensionType: fileExtensionType,
    };
    return this.http.post<any>(url, persistData);
  }

  private static setMachineParameters(options: any, filters: AuditFilters, serialNumber: string) {
    options.params["objectTypes"] = 'MACHINE';
    options.params["objectId"] = serialNumber;
    options = AuditsService.setBasicParameters(options, filters);
    return options;
  }

  private static setBasicParameters(options: any, filters: AuditFilters) {
    options.params["username"] = FiltersUtils.getFilterTrim(filters.username);
    options.params["organizationUuids"] = FiltersUtils.getFilter(filters.organizationUuids);
    options.params["from"] = FiltersUtils.getFilter(filters.from);
    options.params["until"] = FiltersUtils.getFilter(filters.until);
    return options;
  }

}
